import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Gauge, PieChart, Timer, TrendingUp } from "lucide-react";

import { StatusProcessamentoIA, TipoProcessamentoIAMap } from "@workflow/types";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { NumberKpiCard } from "@/components/shared/NumberKpiCard";
import { processamentoIaQueries } from "@/services/api/queries/processamento-ia.queries";

import { STATUS_PROCESSAMENTO_CHART_CONFIG } from "./processamento-ia-visual";

const TENDENCIA_CHART_CONFIG = {
  sucesso: { label: "Sucesso", color: "#10b981" },
  falha: { label: "Falha", color: "#ef4444" },
} satisfies ChartConfig;

function formatarDuracao(segundos: number | null) {
  if (segundos === null) return "—";
  if (segundos < 60) return `${segundos.toFixed(1)}s`;

  const minutos = Math.floor(segundos / 60);
  const restoSegundos = Math.round(segundos % 60);
  return `${minutos}m ${restoSegundos}s`;
}

function taxaSucessoVariant(taxa: number) {
  if (taxa >= 90) return "emerald" as const;
  if (taxa >= 70) return "amber" as const;
  return "destructive" as const;
}

export function ProcessamentoIAEstatisticasPanel() {
  const { data, isLoading } = useQuery(processamentoIaQueries.estatisticas());

  const porTipo = useMemo(
    () =>
      (data?.porTipo ?? []).map((tipo) => ({
        tipoLabel: TipoProcessamentoIAMap[tipo.tipoProcessamento],
        [StatusProcessamentoIA.CONCLUIDO]: tipo.sucesso,
        [StatusProcessamentoIA.FALHA]: tipo.falha,
        [StatusProcessamentoIA.PENDENTE]: tipo.pendente,
      })),
    [data],
  );

  const statusKeys = useMemo(() => Object.keys(STATUS_PROCESSAMENTO_CHART_CONFIG), []);

  const tendencia = useMemo(
    () =>
      (data?.tendenciaDiaria ?? []).map((dia) => ({
        ...dia,
        label: format(new Date(`${dia.dia}T00:00:00`), "dd/MM", { locale: ptBR }),
      })),
    [data],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!data || data.porTipo.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 text-center">
        <PieChart className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          Nenhum processamento de IA registrado para exibir estatísticas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <NumberKpiCard
          title="Taxa de sucesso"
          value={`${data.taxaSucesso.toFixed(1)}%`}
          icon={Gauge}
          variant={taxaSucessoVariant(data.taxaSucesso)}
          hint="Entre os processamentos já finalizados"
        />
        <NumberKpiCard
          title="Tempo médio de processamento"
          value={formatarDuracao(data.tempoMedioSegundos)}
          icon={Timer}
          variant="blue"
          hint="Média dos processamentos concluídos com sucesso"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-3 flex items-center gap-2">
            <PieChart className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">Processamentos por tipo</p>
          </div>

          <ChartContainer
            config={STATUS_PROCESSAMENTO_CHART_CONFIG}
            className="!aspect-auto h-64 w-full"
          >
            <BarChart
              data={porTipo}
              layout="vertical"
              barCategoryGap="30%"
              margin={{ left: 0, right: 16, top: 4, bottom: 4 }}
            >
              <defs>
                {statusKeys.map((status) => (
                  <linearGradient
                    key={status}
                    id={`fillProcessamento-${status}`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop offset="0%" stopColor={`var(--color-${status})`} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={`var(--color-${status})`} stopOpacity={0.55} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                horizontal={false}
                strokeDasharray="3 3"
                className="stroke-border/50"
              />
              <XAxis
                type="number"
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                className="text-[11px]"
              />
              <YAxis
                type="category"
                dataKey="tipoLabel"
                tickLine={false}
                axisLine={false}
                width={140}
                className="text-[11px]"
              />
              <ChartTooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                content={<ChartTooltipContent />}
              />
              {statusKeys.map((status, index) => {
                const isFirst = index === 0;
                const isLast = index === statusKeys.length - 1;
                return (
                  <Bar
                    key={status}
                    dataKey={status}
                    stackId="status"
                    fill={`url(#fillProcessamento-${status})`}
                    maxBarSize={26}
                    radius={[isFirst ? 4 : 0, isLast ? 4 : 0, isLast ? 4 : 0, isFirst ? 4 : 0]}
                  />
                );
              })}
              <ChartLegend content={<ChartLegendContent />} />
            </BarChart>
          </ChartContainer>
        </div>

        <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md">
          <div className="mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-semibold text-foreground">
              Tendência diária <span className="font-normal text-muted-foreground">(14 dias)</span>
            </p>
          </div>

          <ChartContainer config={TENDENCIA_CHART_CONFIG} className="!aspect-auto h-64 w-full">
            <BarChart data={tendencia} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="fillTendencia-sucesso" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-sucesso)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="var(--color-sucesso)" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="fillTendencia-falha" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-falha)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="var(--color-falha)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                className="text-[11px]"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={28}
                allowDecimals={false}
                className="text-[11px]"
              />
              <ChartTooltip
                cursor={{ fill: "hsl(var(--muted))" }}
                content={<ChartTooltipContent />}
              />
              <Bar
                dataKey="sucesso"
                stackId="status"
                fill="url(#fillTendencia-sucesso)"
                maxBarSize={22}
                radius={[0, 0, 3, 3]}
              />
              <Bar
                dataKey="falha"
                stackId="status"
                fill="url(#fillTendencia-falha)"
                maxBarSize={22}
                radius={[3, 3, 0, 0]}
              />
              <ChartLegend content={<ChartLegendContent />} />
            </BarChart>
          </ChartContainer>
        </div>
      </div>
    </div>
  );
}
